import { formatDistanceToNow } from 'date-fns';
import { Link } from 'react-router-dom';
import {
  ChevronRight,
  AlertTriangle,
  BarChart3,
  CalendarClock,
  FileText,
  Headphones,
  Inbox,
  LineChart,
  MessageSquare,
  Mic,
  Network,
  Send,
  ShieldCheck,
  TrendingDown,
  TrendingUp,
  Volume2,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useIsOnline } from '@/hooks/useIsOnline';
import { useCachedQuery } from '@/lib/offline/cachedQuery';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';

// Phase-1 persona home feed (offline-resilient), now wired to the live `mobile_home_feed` RPC.
//
// The offline substrate is unchanged: the RPC read goes through `useCachedQuery`, so last-known-good is
// written through to the persistent cache, a synchronous cached placeholder paints before/without a
// network round-trip, and the page never blanks offline. The queryFn THROWS on `{ error }` so React Query
// keeps the cached placeholder and serves it while offline; the stale affordance is kept verbatim.
//
// A non-research user gets `prebriefs: []`, zero counts and `market_strip: []` from the RPC — that is a
// valid (not an error) state, so each section renders a graceful empty/quiet state rather than an error.
// Only a signed-out caller raises (28000), which surfaces via `feed.isError`.

type Persona = 'cio' | 'pm' | 'analyst' | 'general';

interface PrebriefEntry {
  surface: string;
  target_date: string | null;
  // digest is opaque jsonb; we only read it as a fallback summary, so keep it loosely typed.
  digest: unknown;
  digest_markdown: string | null;
  has_signal: boolean;
  generated_at: string | null;
}

interface MarketStripRow {
  ticker: string;
  label: string | null;
  price: number | null;
  change_pct: number | null;
  change_1d: number | null;
  market_state: string | null;
  epic: string | null;
  updated_at: string | null;
}

interface HomeFeed {
  as_of: string | null;
  prebriefs: PrebriefEntry[];
  triage_count: number;
  drift_count: number;
  market_strip: MarketStripRow[];
}

const EMPTY_FEED: HomeFeed = {
  as_of: null,
  prebriefs: [],
  triage_count: 0,
  drift_count: 0,
  market_strip: [],
};

// Map the (client-side) role_level onto a coarse persona. role_level is the only persona signal we have
// client-side today (no job title) — keep this in lockstep with useAuth's StaffMember.role_level union.
function personaFor(roleLevel: string | undefined): Persona {
  switch (roleLevel) {
    case 'superadmin':
    case 'admin':
      return 'cio';
    case 'adviser':
      return 'pm';
    case 'user':
      return 'analyst';
    default:
      return 'general';
  }
}

const GREETING_BY_PERSONA: Record<Persona, string> = {
  cio: 'Your desk',
  pm: 'Your desk',
  analyst: 'Your desk',
  general: 'Good day',
};

// Live fetch via the `mobile_home_feed` SECURITY DEFINER RPC. The generated supabase types are stale for
// this function, so the call is cast through `any`. MUST THROW on `{ error }` so useCachedQuery keeps the
// cached placeholder (and serves it offline) instead of overwriting it with a partial/empty value.
async function fetchHomeFeed(): Promise<HomeFeed> {
  const { data, error } = await (supabase.rpc as any)('mobile_home_feed');
  if (error) throw error;
  // Defensive normalisation: the RPC always returns the full envelope, but guard arrays/counts so the
  // UI never sees undefined (e.g. if a future payload trims a field).
  const raw = (data ?? {}) as Partial<HomeFeed>;
  return {
    as_of: raw.as_of ?? null,
    prebriefs: Array.isArray(raw.prebriefs) ? raw.prebriefs : [],
    triage_count: typeof raw.triage_count === 'number' ? raw.triage_count : 0,
    drift_count: typeof raw.drift_count === 'number' ? raw.drift_count : 0,
    market_strip: Array.isArray(raw.market_strip) ? raw.market_strip : [],
  };
}

// Turn a surface code into a readable label.
function prettySurface(surface: string): string {
  if (!surface) return 'Brief';
  return surface
    .split(/[_\-\s]+/)
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(' ');
}

// First non-empty line of a prebrief's markdown digest, trimmed of heading/bullet markers; else fall back
// to a coarse digest summary; else a quiet placeholder.
function prebriefSummary(p: PrebriefEntry): string {
  if (p.digest_markdown) {
    const line = p.digest_markdown
      .split('\n')
      .map((l) => l.replace(/^#{1,6}\s*/, '').replace(/^[-*]\s*/, '').trim())
      .find((l) => l.length > 0);
    if (line) return line.length > 160 ? `${line.slice(0, 159)}…` : line;
  }
  if (p.digest && typeof p.digest === 'object') {
    const d = p.digest as Record<string, unknown>;
    const headline = d.headline ?? d.summary ?? d.title;
    if (typeof headline === 'string' && headline.trim()) return headline.trim();
  }
  return 'Brief ready — open Ask to dig in.';
}

function formatRelative(iso: string | null): string | null {
  if (!iso) return null;
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return null;
  }
}

function formatPct(value: number | null): string {
  if (value == null || Number.isNaN(value)) return '—';
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}%`;
}

function formatPrice(value: number | null): string {
  if (value == null || Number.isNaN(value)) return '—';
  return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Deliberate home-screen market preview: a few headline instruments (FTSE 100, S&P 500, GBP/USD, Brent)
// instead of whatever sorts first alphabetically. Each entry matches against the IG ticker so the order
// is stable regardless of the strip's incoming sort; we fall back to filling any remaining slots from the
// front of the strip so the preview is never short when those headline epics are absent for a user.
const PREVIEW_TICKER_PRIORITY = [
  'IG:IX.D.FTSE.DAILY.IP', // FTSE 100
  'IG:IX.D.SPTRD.DAILY.IP', // US 500 (S&P 500)
  'IG:CS.D.GBPUSD.TODAY.IP', // GBP/USD
  'IG:CC.D.LCO.USS.IP', // Oil - Brent Crude
];

function priorityPreview(strip: MarketStripRow[], count = 4): MarketStripRow[] {
  const byTicker = new Map(strip.map((m) => [m.ticker, m] as const));
  const chosen: MarketStripRow[] = [];
  const used = new Set<string>();
  for (const t of PREVIEW_TICKER_PRIORITY) {
    const row = byTicker.get(t);
    if (row && !used.has(row.ticker)) {
      chosen.push(row);
      used.add(row.ticker);
    }
    if (chosen.length >= count) return chosen;
  }
  // Top up from the front of the strip if any headline epics weren't present.
  for (const row of strip) {
    if (chosen.length >= count) break;
    if (!used.has(row.ticker)) {
      chosen.push(row);
      used.add(row.ticker);
    }
  }
  return chosen;
}

export default function MobileHome() {
  const { staffMember } = useAuth();
  const online = useIsOnline();
  const first = staffMember?.first_name || 'there';
  const roleLevel = staffMember?.role_level;

  // Scope the cache key to the persona so a CIO and an analyst don't read each other's last-known feed.
  const persona = personaFor(roleLevel);

  const feed = useCachedQuery<HomeFeed>({
    queryKey: ['mobile-home-feed', persona],
    cacheKey: `mobile.home_feed.${persona}`,
    queryFn: fetchHomeFeed,
  });

  // Never blank offline: prefer fresh/placeholder data, then the synchronous cached envelope is already
  // seeded by useCachedQuery's placeholderData, and finally fall back to an empty feed so a cold offline
  // start with an empty cache still renders the full shell (with quiet empty states) rather than nothing.
  const data = feed.data ?? EMPTY_FEED;
  const { prebriefs, triage_count, drift_count, market_strip } = data;

  // Show a "showing saved data" affordance when we're serving last-known-good (offline or pre-fetch).
  const showStale = feed.isFromCache && feed.cachedAt != null;
  let staleLabel: string | null = null;
  if (showStale && feed.cachedAt) {
    try {
      staleLabel = `Saved ${formatDistanceToNow(new Date(feed.cachedAt), { addSuffix: true })}`;
    } catch {
      staleLabel = 'Showing saved data';
    }
  }

  // A signed-out caller is the only thing the RPC treats as an error (28000). Anything that isn't a hard
  // error (incl. a non-research user with empty arrays) renders normally. Only show an error banner when
  // we have no cached data to fall back on either.
  const hardError = feed.isError && !feed.isFromCache && feed.data === undefined;

  // Surface preview of the strip — keep it compact on a phone. Deliberate priority order (FTSE / S&P /
  // GBP-USD / Brent) rather than the strip's alphabetical sort; the full 34-instrument list lives on
  // /partner/m/markets.
  const stripPreview = priorityPreview(market_strip, 4);
  const hasResearchSignal =
    prebriefs.length > 0 || triage_count > 0 || drift_count > 0 || market_strip.length > 0;

  return (
    <div className="min-h-screen bg-background px-4 pb-8 pt-[calc(1.25rem+env(safe-area-inset-top))]">
      <header className="mb-4">
        <p className="text-sm text-muted-foreground">{GREETING_BY_PERSONA[persona]},</p>
        <h1 className="font-heading text-2xl font-semibold text-primary">{first}</h1>
        {showStale && (
          <p
            className={cn(
              'mt-1 text-[11px] font-medium uppercase tracking-wide',
              online ? 'text-muted-foreground/70' : 'text-amber-600',
            )}
          >
            {staleLabel}
          </p>
        )}
      </header>

      <div className="space-y-3">
        {hardError && (
          <section
            className="rounded-xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-900/40 dark:bg-amber-900/20"
            role="alert"
          >
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
              <h2 className="font-heading text-sm font-semibold text-amber-800 dark:text-amber-200">
                Couldn’t load your feed
              </h2>
            </div>
            <p className="mt-1 text-sm text-amber-700 dark:text-amber-300/90">
              {online
                ? 'Please sign in again, then pull back to Home.'
                : 'You’re offline and there’s no saved feed yet. This will fill in once you reconnect.'}
            </p>
          </section>
        )}

        {/* Stat chips: triage + drift. Tappable into the inbox. */}
        <div className="grid grid-cols-2 gap-3">
          <Link
            to="/partner/m/inbox"
            className="rounded-xl border border-border bg-card p-4 transition-colors active:bg-muted/50"
            aria-label={`${triage_count} item${triage_count === 1 ? '' : 's'} to triage`}
          >
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground/70">To triage</p>
            <p className="mt-1 font-heading text-2xl font-semibold text-primary tabular-nums">
              {triage_count}
            </p>
          </Link>
          <Link
            to="/partner/m/inbox"
            className="rounded-xl border border-border bg-card p-4 transition-colors active:bg-muted/50"
            aria-label={`${drift_count} drift alert${drift_count === 1 ? '' : 's'}`}
          >
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground/70">Drift alerts</p>
            <p
              className={cn(
                'mt-1 font-heading text-2xl font-semibold tabular-nums',
                drift_count > 0 ? 'text-amber-600' : 'text-primary',
              )}
            >
              {drift_count}
            </p>
          </Link>
        </div>

        {/* Daily brief — latest prebrief per surface. */}
        <section className="rounded-xl border border-border bg-card p-4">
          <div className="flex items-center justify-between gap-2">
            <h2 className="font-heading text-sm font-semibold">Daily brief</h2>
            {/* AUD-4: entry into the daily audio-brief player. Always reachable — the player handles its
                own empty state when no episode has been generated yet. */}
            <Link
              to="/partner/m/brief"
              className="inline-flex shrink-0 items-center gap-1 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary active:bg-primary/20"
              aria-label="Listen to the daily audio brief"
            >
              <Volume2 className="h-3.5 w-3.5" aria-hidden="true" />
              Listen
            </Link>
          </div>
          {prebriefs.length === 0 ? (
            <p className="mt-1 text-sm text-muted-foreground">
              No briefs for you yet — your role-shaped morning brief will appear here when it’s ready.
            </p>
          ) : (
            <ul className="mt-2 space-y-2">
              {prebriefs.map((p) => {
                const when = formatRelative(p.generated_at);
                return (
                  <li key={p.surface}>
                    {/* Tappable into the full digest (MobileBriefDetail) — the home card shows only a
                        one-line summary; the detail screen renders the whole digest_markdown. */}
                    <Link
                      to={`/partner/m/brief-detail/${encodeURIComponent(p.surface)}`}
                      className="block rounded-lg bg-muted/40 p-3 transition-colors active:bg-muted/70"
                      aria-label={`Open the full ${prettySurface(p.surface)} brief`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="font-heading text-sm font-semibold">
                          {prettySurface(p.surface)}
                        </span>
                        {p.has_signal && (
                          <span
                            className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary"
                            title="New signal in this brief"
                          >
                            signal
                          </span>
                        )}
                        <ChevronRight
                          className="ml-auto h-4 w-4 shrink-0 text-muted-foreground/50"
                          aria-hidden="true"
                        />
                      </div>
                      <p className="mt-1 text-sm text-muted-foreground">{prebriefSummary(p)}</p>
                      {when && (
                        <p className="mt-1 text-[11px] uppercase tracking-wide text-muted-foreground/70">
                          Updated {when}
                        </p>
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* Markets — compact priority preview; the full 34-instrument watch list is on /markets. */}
        <section className="rounded-xl border border-border bg-card p-4">
          <div className="flex items-center justify-between gap-2">
            <h2 className="font-heading text-sm font-semibold">Live prices</h2>
            {market_strip.length > stripPreview.length && (
              <Link
                to="/partner/m/markets"
                className="inline-flex shrink-0 items-center gap-0.5 text-xs font-semibold text-primary active:opacity-80"
                aria-label={`View all ${market_strip.length} instruments`}
              >
                View all {market_strip.length}
                <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
              </Link>
            )}
          </div>
          {stripPreview.length === 0 ? (
            <p className="mt-1 text-sm text-muted-foreground">
              Live market data isn’t available for your access level.
            </p>
          ) : (
            <ul className="mt-2 divide-y divide-border/60">
              {stripPreview.map((m) => {
                const up = (m.change_pct ?? 0) >= 0;
                return (
                  <li key={m.ticker} className="flex items-center justify-between gap-3 py-2">
                    <span className="min-w-0 flex-1 truncate text-sm" title={m.label ?? m.ticker}>
                      {m.label ?? m.ticker}
                    </span>
                    <span className="shrink-0 font-mono text-sm tabular-nums">{formatPrice(m.price)}</span>
                    <span
                      className={cn(
                        'flex w-16 shrink-0 items-center justify-end gap-0.5 font-mono text-xs tabular-nums',
                        m.change_pct == null
                          ? 'text-muted-foreground'
                          : up
                            ? 'text-green-600'
                            : 'text-red-600',
                      )}
                    >
                      {m.change_pct != null &&
                        (up ? (
                          <TrendingUp className="h-3 w-3" aria-hidden="true" />
                        ) : (
                          <TrendingDown className="h-3 w-3" aria-hidden="true" />
                        ))}
                      {formatPct(m.change_pct)}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* Explore — entry points into the deeper research surfaces. */}
        <section aria-labelledby="explore-heading" className="space-y-3">
          <h2
            id="explore-heading"
            className="px-1 pt-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground/70"
          >
            Explore
          </h2>
          <NavCard
            to="/partner/m/markets"
            icon={<BarChart3 className="h-5 w-5" aria-hidden="true" />}
            title="Live prices"
            body="The full IG watch list — every instrument, grouped by asset class."
          />
          <NavCard
            to="/partner/m/research"
            icon={<FileText className="h-5 w-5" aria-hidden="true" />}
            title="Research"
            body="Browse recent research — bodies, house views and risks."
          />
          <NavCard
            to="/partner/m/outputs"
            icon={<Send className="h-5 w-5" aria-hidden="true" />}
            title="Outputs"
            body="What research has shipped recently — comms, briefs and docs."
          />
          <NavCard
            to="/partner/m/predictions"
            icon={<TrendingUp className="h-5 w-5" aria-hidden="true" />}
            title="Predictions"
            body="The macro track record — calls made and how they played out."
          />
          <NavCard
            to="/partner/m/kg"
            icon={<Network className="h-5 w-5" aria-hidden="true" />}
            title="Knowledge graph"
            body="Explore an entity — its key facts and how it connects."
          />
          <NavCard
            to="/partner/m/podcast"
            icon={<Headphones className="h-5 w-5" aria-hidden="true" />}
            title="Podcast"
            body="The Capital and Context Podcast — weekly episodes, in the app."
          />
        </section>

        {/* Navigation cards into the other mobile surfaces. */}
        <nav className="space-y-3" aria-label="Quick links">
          <NavCard
            to="/partner/m/ask"
            icon={<MessageSquare className="h-5 w-5" aria-hidden="true" />}
            title="Ask"
            body="Put a single question to research and get a cited answer."
          />
          <NavCard
            to="/partner/m/prep"
            icon={<CalendarClock className="h-5 w-5" aria-hidden="true" />}
            title="Meeting prep"
            body="Agenda and the relevant brief for your next meeting."
          />
          <NavCard
            to="/partner/m/portfolio"
            icon={<LineChart className="h-5 w-5" aria-hidden="true" />}
            title="Portfolio glance"
            body="Returns, benchmark and drawdown across your portfolios."
          />
          <NavCard
            to="/partner/m/compliance"
            icon={<ShieldCheck className="h-5 w-5" aria-hidden="true" />}
            title="Compliance"
            body="RAG traffic-light across the book — open issues at a glance."
          />
          <NavCard
            to="/partner/m/inbox"
            icon={<Inbox className="h-5 w-5" aria-hidden="true" />}
            title="Inbox"
            body="Captures, drift alerts and approvals awaiting review."
          />
          <NavCard
            to="/partner/m/captures"
            icon={<Mic className="h-5 w-5" aria-hidden="true" />}
            title="My Captures"
            body="Your own recordings, memos and notes — with live processing status."
          />
        </nav>

        {!hasResearchSignal && !hardError && (
          <p className="px-1 pt-1 text-center text-[11px] uppercase tracking-wide text-muted-foreground/60">
            More appears here as research lands for you
          </p>
        )}
      </div>
    </div>
  );
}

interface NavCardProps {
  to: string;
  icon: React.ReactNode;
  title: string;
  body: string;
}

function NavCard({ to, icon, title, body }: NavCardProps) {
  return (
    <Link
      to={to}
      className="flex items-center gap-3 rounded-xl border border-border bg-card p-4 transition-colors active:bg-muted/50"
    >
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block font-heading text-sm font-semibold">{title}</span>
        <span className="mt-0.5 block text-sm text-muted-foreground">{body}</span>
      </span>
      <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground/60" aria-hidden="true" />
    </Link>
  );
}
